import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { UsersService } from '../users.service'
import { UsersRepository } from '../users.repository'
import { UserSearchParams } from '../models/user-search.model'
import { User } from '../models/user.entity'
import {
  UsersListingData,
  UsersListingPage,
} from '../models/user-listing.model'
import { parseUsersPage } from '../libs/user-search.lib'
import { Observable } from 'rxjs'
import { RolesService } from '../../roles'
import { mergeById } from '../../../libs/filter.lib'

interface UsersListingState {
  filters?: UserSearchParams
  data?: User[]
  extData?: UsersListingData
  dropdownOptions?: UsersListingData
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  searchString?: string
}

const USERS_LISTING_INITIAL_STATE: UsersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class UsersListingStore {
  constructor(
    private usersService: UsersService,
    private usersRepository: UsersRepository,
    private rolesService: RolesService,
  ) {}

  private state = signal<UsersListingState>(USERS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly users = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(
    tenantId: string,
    params?: UserSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.usersRepository
        .searchUsers$(
          this.parseStateFilterParams(this.state(), params),
          tenantId,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(parseUsersPage(data, tenantId))
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async delete(user: User): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.usersRepository.alert('USER_DELETION', user).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.usersService.delete$(user._id).subscribe({
            next: () => {
              resolve(true)
              this.usersRepository.notify({
                code: 'USER_DELETED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  async eject(user: User): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.usersRepository.alert('USER_EJECTION', user).then((response) => {
        if (response === 'success') {
          this.usersService.eject$(user._id).subscribe({
            next: () => {
              resolve(true)
              this.usersRepository.notify({
                code: 'USER_EJECTED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof UsersListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<NonNullable<UsersListingData[LookupKey]>>

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      roles: () => this.rolesService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: UsersListingPage) {
    const extData = this.extData()
    const newExtData = {
      roles: mergeById(extData?.roles, data.extData?.roles),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: UserSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: UserSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  removeFilter(filter: UserSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof UserSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: UsersListingState,
    filters?: UserSearchParams,
  ): UserSearchParams {
    const params: UserSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }

    return params
  }
}
